import { useState } from "react";
import { ZoomableGroup, ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import chroma from "chroma-js";
import choroplethMapStyles from "./choropleth-map-styles";
import { Box, IconButton, Typography } from "@material-ui/core";
import ReactTooltip from "react-tooltip";
import { useTranslation } from "react-i18next";
import { countries } from "./countries";

export interface MapProps {
	property: string;
	data: Array<{ [key: string]: unknown }>;
	height?: number;
	width?: number;
	minZoom?: number;
	maxZoom?: number;
	zoomPower?: number;
	defaultZoom?: number;
	defaultCoordinates?: Array<number>;
	colorRange?: Array<string>;
}

interface Geography {
	type: string;
	properties: {
		[key: string]: unknown;
	};
	svgPath: string;
	geometry: {
		type: string;
		coordinates: Array<any>;
	};
	rsmKey: string;
}

interface Position {
	coordinates: Array<number>;
	zoom: number;
}

const ChoroplethMap = ({ property, data, height, width, minZoom, maxZoom, zoomPower, defaultZoom, defaultCoordinates }: MapProps) => {
	const [tooltipContent, setTooltipContent] = useState<string>("");
	const { controls, controlButton, noAgreementsSign } = choroplethMapStyles();
	const [position, setPosition] = useState({ coordinates: defaultCoordinates || [0, 0], zoom: defaultZoom || 1 });
	const sortedDataByProperty = new Set(data?.map((item) => item[property]).sort((a: any, b: any) => a - b));
	const mapHeight = height || 404;
	const mapWidth = width || 808;
	const startColor = "#367bf5";
	const endColor = "#cfe0f8";
	const { t } = useTranslation();
	function handleZoomIn() {
		if (position.zoom >= 4) return;
		setPosition((pos) => ({ ...pos, zoom: pos.zoom * (zoomPower || 2) }));
	}

	function handleZoomOut() {
		if (position.zoom <= 1) return;
		setPosition((pos) => ({ ...pos, zoom: pos.zoom / (zoomPower || 2) }));
	}

	function handleMoveEnd(position: Position) {
		setPosition(position);
	}

	const generateColorRange = (numberOfColors: number) => {
		const colorRange = [];

		for (let i = 0; i < numberOfColors; i++) {
			const interpolationFactor = i / (numberOfColors - 1);
			const color = chroma.mix(endColor, startColor, interpolationFactor, "lch");
			colorRange.push(color.hex());
		}

		return colorRange;
	};
	const colors = generateColorRange(sortedDataByProperty.size);
	const colorScale =
		sortedDataByProperty.size < 2
			? () => startColor
			: scaleLinear()
					.domain(sortedDataByProperty as unknown as Array<number>)
					.range(colors as any);

	return (
		<Box
			style={{ backgroundColor: "white" }}
			height={mapHeight}
			minWidth={mapWidth}
			ml={4}
			mt={2}
			borderRadius="4px"
			border="1px solid #74bcff"
			position="relative"
		>
			<div>
				{!data.length && <span className={noAgreementsSign}>{t("no-public-agreements-found")}</span>}
				<ComposableMap
					style={{ outline: "none" }}
					data-tip=""
					projectionConfig={{ scale: 147 }}
					height={mapHeight - 2}
					width={mapWidth - 2}
				>
					<ZoomableGroup
						minZoom={minZoom}
						maxZoom={maxZoom}
						zoom={position.zoom}
						center={position.coordinates as any}
						onMoveEnd={handleMoveEnd}
					>
						{
							<Geographies geography={countries}>
								{({ geographies }) =>
									geographies.map((geo) => {
										const d = data.find((s) => s["ISO_A3"] === geo["id"]);
										const propertyValue = d && d[property];

										return (
											<Geography
												key={geo.rsmKey}
												geography={geo}
												fill={d ? (colorScale(propertyValue as any) as any) : "#F5F8F9"}
												stroke="#74bcff"
												strokeWidth="0.5px"
												onMouseEnter={() => {
													if (d !== undefined) {
														const { name } = geo.properties;
														setTooltipContent(`${name}: ${propertyValue}`);
													}
												}}
												style={{
													default: {
														outline: "none",
													},
													hover: {
														outline: "none",
													},
													pressed: {
														outline: "none",
													},
												}}
												onMouseLeave={() => {
													setTooltipContent("");
												}}
											/>
										);
									})
								}
							</Geographies>
						}
					</ZoomableGroup>
				</ComposableMap>
				<Box display="flex" flexDirection="column" className={controls}>
					<IconButton size="small" onClick={handleZoomIn} className={controlButton} style={{ marginBottom: "5px" }}>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke="#367bf5" strokeWidth="3">
							<line x1="12" y1="5" x2="12" y2="19" />
							<line x1="5" y1="12" x2="19" y2="12" />
						</svg>
					</IconButton>
					<IconButton size="small" onClick={handleZoomOut} className={controlButton}>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke="#367bf5" strokeWidth="3">
							<line x1="5" y1="12" x2="19" y2="12" />
						</svg>
					</IconButton>
				</Box>
			</div>
			<ReactTooltip backgroundColor="rgba(14, 35, 54, 0.9)">
				{tooltipContent !== "" && <Typography>{tooltipContent}</Typography>}
			</ReactTooltip>
		</Box>
	);
};

export default ChoroplethMap;
